import "@diligentcorp/atlas-web-component-icons/lib/avatar/index.js";
import { msg, updateWhenLocaleChanges } from "@lit/localize";
import { css, html } from "lit";
import { when } from "lit/directives/when.js";
import { LINKS } from "../configuration/constants.js";
import { defineComponent } from "../configuration/utils.js";
import { Menu } from "./Menu.js";
import { coreVar, semanticVar } from "./styles.js";
/**
 * The `atlas-gn-user-menu` component is a menu that displays the user's name and organization name.
 * It also displays links to the user's profile and a sign out link.
 * The component is only visible if the user is logged in.
 *
 * @tag atlas-gn-user-menu
 * @prop {string} userFullName - The user's full name.
 * @prop {string} customerName - The name of the organization the user is a member of.
 * @prop {boolean} enableProfile - Whether to display the profile links.
 * @prop {string} profile - The url of the user's profile.
 * @prop {string} groups - The url of groups page.
 * @prop {string} logout - The url to sign out.
 * @prop {boolean} open - Whether the menu is open.
 * @fires {CustomEvent<UserMenu>} atlas-gn:open-menu
 */
export class UserMenu extends Menu {
    constructor() {
        super();
        this.userFullName = "";
        this.customerName = "";
        this.enableProfile = false;
        this.profileSettings = "";
        updateWhenLocaleChanges(this);
    }
    renderButtonContent() {
        return html `<dil-avatar-icon aria-hidden="true"></dil-avatar-icon>`;
    }
    renderMenu() {
        return html `
      <header>
        <h2>${this.userFullName}</h2>
        <p>${this.customerName}</p>
      </header>
      ${when(this.enableProfile, () => {
            LINKS.PROFILE.loadIcon();
            LINKS.LOGOUT.loadIcon();
            LINKS.PROFILE_SETTINGS.loadIcon();
            LINKS.GROUPS.loadIcon();
            return html `
          <ul role="menu">
            ${this.renderMenuItem({
                label: LINKS.PROFILE.label(),
                icon: LINKS.PROFILE.icon(),
                url: this.profile,
            })}
            ${this.profileSettings &&
                this.renderMenuItem({
                    label: LINKS.PROFILE_SETTINGS.label(),
                    icon: LINKS.PROFILE_SETTINGS.icon(),
                    url: this.profileSettings,
                })}
            ${this.groups &&
                this.renderMenuItem({
                    label: LINKS.GROUPS.label(),
                    icon: LINKS.GROUPS.icon(),
                    url: this.groups,
                })}
            ${this.renderDivider()}
            ${this.renderMenuItem({
                label: LINKS.LOGOUT.label(),
                icon: LINKS.LOGOUT.icon(),
                url: this.logout,
                destructive: true,
            })}
          </ul>
        `;
        })}
    `;
    }
    get menuId() {
        return "user-menu";
    }
    get menuAriaLabel() {
        return msg(`Profile menu`);
    }
    get menuButtonArialLabel() {
        return msg(`Open profile menu`);
    }
}
UserMenu.properties = {
    ...Menu.properties,
    userFullName: { type: String, reflect: true, attribute: "user-full-name" },
    customerName: { type: String, reflect: true, attribute: "customer-name" },
    enableProfile: { type: Boolean, reflect: true, attribute: "enable-profile" },
    profile: { type: String, reflect: true, attribute: "profile" },
    logout: { type: String, reflect: true, attribute: "logout" },
    profileSettings: { type: String, reflect: true, attribute: "profile-settings" },
    groups: { type: String, reflect: true, attribute: "groups" },
};
UserMenu.styles = [
    Menu.styles,
    css `
      dialog {
        max-width: 100svw;
      }
      header {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        padding: ${coreVar("spacing-2")};
        gap: 0;
      }
      header h2,
      header p {
        margin: ${coreVar("spacing-0")};
        padding: ${coreVar("spacing-0")};
        white-space: normal;
      }
      header h2 {
        font-size: ${semanticVar("font-text-body-font-size")};
        font-weight: ${semanticVar("font-weight-emphasis")};
        line-height: ${semanticVar("font-text-body-line-height")};
        letter-spacing: ${semanticVar("font-text-body-letter-spacing")};
      }
      header p {
        color: ${semanticVar("color-type-muted")};
      }

      ul {
        border-top-width: ${semanticVar("border-width-thin")};
        border-top-color: ${semanticVar("color-ui-divider-default")};
        border-top-style: solid;
      }
    `,
];
export const USER_MENU_TAG = "atlas-gn-user-menu";
defineComponent(USER_MENU_TAG, UserMenu);
